import { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { cn } from "@voicerun/design-system";

import { Button, Card, CheckboxField, InputField, SettingsList, SettingsListItem, Spinner, Text, View } from "../base";
import { Icon } from "../base/Icon";
import { Dialog } from "../base/Dialog";
import { ConfirmDialog } from "../ConfirmDialog";
import { OrganizationSettingsTab } from "./OrganizationSettingTab";
import { useAppDispatch, useAppSelector } from "../../redux";
import { organizationServiceAccountsActions, OrganizationServiceAccount } from "../../redux/reducers/organizationServiceAccountsSlice";
import { organizationServiceAccountTokensActions, OrganizationServiceAccountToken } from "../../redux/reducers/organizationServiceAccountTokensSlice";
import { permissionRegistryActions, PermissionEntry } from "../../redux/reducers/permissionRegistrySlice";

type Props = {
  className?: string;
};

type GroupedPermissions = { group: string; entries: PermissionEntry[] }[];

/**
 * Service account management.
 *
 * Each service account is an identity with an explicit permission set; tokens
 * are credentials attached to it. Token rotation: mint new, distribute, revoke
 * old. Identity stays stable, so consumers don't have to be re-permissioned.
 */
export const OrganizationServiceAccountsCard = ({ className }: Props) => {
  const dispatch = useAppDispatch();

  const accountEntities = useAppSelector((state) => state.organizationServiceAccounts.entities);
  const accountIds = useAppSelector((state) => state.organizationServiceAccounts.ids);
  const isLoadingAccounts = useAppSelector((state) => state.organizationServiceAccounts.isLoading);
  const isCreatingAccount = useAppSelector((state) => state.organizationServiceAccounts.isLoadingCreate);
  const accounts = useMemo(() => accountIds.map((id) => accountEntities[id]).filter(Boolean) as OrganizationServiceAccount[], [accountIds, accountEntities]);

  const tokensByAccount = useAppSelector((state) => state.organizationServiceAccountTokens.byAccount);

  const registryEntries = useAppSelector((state) => state.permissionRegistry.entries);
  const registryLoaded = useAppSelector((state) => state.permissionRegistry.loaded);

  // Dialog visibility + the SA that each dialog is operating on.
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingAccount, setEditingAccount] = useState<OrganizationServiceAccount | null>(null);
  const [tokensAccount, setTokensAccount] = useState<OrganizationServiceAccount | null>(null);

  // One-time reveal after a mint.
  const [revealedToken, setRevealedToken] = useState<string | null>(null);
  const [revealedKeyContext, setRevealedKeyContext] = useState<{ accountName: string; tokenName: string | null } | null>(null);
  const [tokenCopied, setTokenCopied] = useState(false);

  // Destructive confirms.
  const [deleteAccountId, setDeleteAccountId] = useState<string | null>(null);
  const [deleteToken, setDeleteToken] = useState<{ serviceAccountId: string; tokenId: string } | null>(null);

  // Kebab menu state (mirrors MembersCard's per-row menu with portal positioning).
  const [openMenuAccountId, setOpenMenuAccountId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const triggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // Form state lives at the card level so a single picker component can serve
  // both the create and edit dialogs without prop drilling.
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formPermissions, setFormPermissions] = useState<Set<string>>(new Set());
  const [formError, setFormError] = useState<string | null>(null);

  // Token-modal local state — separated so toggling it doesn't disturb the form.
  const [newTokenName, setNewTokenName] = useState("");
  const [isMinting, setIsMinting] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  useEffect(() => {
    dispatch(organizationServiceAccountsActions.get({}));
    if (!registryLoaded) {
      dispatch(permissionRegistryActions.fetch());
    }
  }, [dispatch, registryLoaded]);

  // Close the per-row kebab menu on outside click.
  useEffect(() => {
    if (!openMenuAccountId) return;
    const handler = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current && !menuRef.current.contains(target) && !triggerRefs.current[openMenuAccountId]?.contains(target)) {
        setOpenMenuAccountId(null);
        setMenuPosition(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openMenuAccountId]);

  // The registry endpoint already filters hidden / internal / user-scoped
  // entries. Deprecated entries arrive with a "DEPRECATED:" description prefix
  // applied server-side; we additionally bucket them into a synthetic "Legacy"
  // group rendered at the bottom of the picker, so customers don't have to
  // hunt through their original groups to find what's on the way out.
  const groupedPermissions: GroupedPermissions = useMemo(() => {
    const groups = new Map<string, PermissionEntry[]>();
    const legacy: PermissionEntry[] = [];
    for (const entry of registryEntries) {
      if (entry.deprecated) {
        legacy.push(entry);
        continue;
      }
      const list = groups.get(entry.group) ?? [];
      list.push(entry);
      groups.set(entry.group, list);
    }
    const result = Array.from(groups.entries()).map(([group, entries]) => ({ group, entries }));
    if (legacy.length > 0) {
      result.push({ group: "Legacy", entries: legacy });
    }
    return result;
  }, [registryEntries]);

  const resetForm = () => {
    setFormName("");
    setFormDescription("");
    setFormPermissions(new Set());
    setFormError(null);
  };

  const toggleMenu = (accountId: string) => {
    if (openMenuAccountId === accountId) {
      setOpenMenuAccountId(null);
      setMenuPosition(null);
      return;
    }
    const trigger = triggerRefs.current[accountId];
    if (trigger) {
      const rect = trigger.getBoundingClientRect();
      setMenuPosition({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    }
    setOpenMenuAccountId(accountId);
  };

  const closeMenu = () => {
    setOpenMenuAccountId(null);
    setMenuPosition(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setShowCreateDialog(true);
  };

  const openEditDialog = (account: OrganizationServiceAccount) => {
    closeMenu();
    setFormName(account.name);
    setFormDescription(account.description ?? "");
    setFormPermissions(new Set(account.permissions));
    setFormError(null);
    setEditingAccount(account);
  };

  const openTokensDialog = (account: OrganizationServiceAccount) => {
    closeMenu();
    setNewTokenName("");
    setTokensAccount(account);
    // Lazy-load tokens for this account if we haven't yet.
    if (!tokensByAccount[account.id]) {
      dispatch(organizationServiceAccountTokensActions.list({ serviceAccountId: account.id }));
    }
  };

  const openDeleteDialog = (account: OrganizationServiceAccount) => {
    closeMenu();
    setDeleteAccountId(account.id);
  };

  const handleCreate = async () => {
    setFormError(null);
    const trimmedName = formName.trim();
    if (!trimmedName) {
      setFormError("Name is required");
      return;
    }
    if (formPermissions.size === 0) {
      setFormError("Select at least one permission");
      return;
    }

    const result = await dispatch(
      organizationServiceAccountsActions.create({
        name: trimmedName,
        description: formDescription.trim() || undefined,
        permissions: Array.from(formPermissions),
      } as Partial<OrganizationServiceAccount>),
    );

    if (organizationServiceAccountsActions.create.rejected.match(result)) {
      setFormError(result.error.message ?? "Failed to create service account");
      return;
    }

    setShowCreateDialog(false);
    resetForm();
  };

  const handleSaveEdit = async () => {
    if (!editingAccount) return;
    setFormError(null);
    const trimmedName = formName.trim();
    if (!trimmedName) {
      setFormError("Name is required");
      return;
    }
    if (formPermissions.size === 0) {
      setFormError("Select at least one permission");
      return;
    }

    setIsSavingEdit(true);
    try {
      const result = await dispatch(
        organizationServiceAccountsActions.updateById({
          id: editingAccount.id,
          name: trimmedName,
          description: formDescription.trim() || null,
          permissions: Array.from(formPermissions),
        } as Partial<OrganizationServiceAccount>),
      );

      if (organizationServiceAccountsActions.updateById.rejected.match(result)) {
        setFormError(result.error.message ?? "Failed to update service account");
        return;
      }

      setEditingAccount(null);
      resetForm();
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleMintToken = async () => {
    if (!tokensAccount) return;
    const name = newTokenName.trim();
    setIsMinting(true);
    try {
      const result = await dispatch(
        organizationServiceAccountTokensActions.create({
          serviceAccountId: tokensAccount.id,
          name: name || undefined,
        }),
      );
      if (organizationServiceAccountTokensActions.create.rejected.match(result)) {
        window.alert(result.error.message ?? "Failed to mint token");
        return;
      }
      const created = result.payload?.token;
      if (created?.token) {
        setRevealedToken(created.token);
        setRevealedKeyContext({ accountName: tokensAccount.name, tokenName: created.name });
      }
      setNewTokenName("");
    } finally {
      setIsMinting(false);
    }
  };

  const handleConfirmDeleteAccount = async () => {
    if (deleteAccountId) {
      await dispatch(organizationServiceAccountsActions.deleteById({ id: deleteAccountId }));
      setDeleteAccountId(null);
    }
  };

  const handleConfirmDeleteToken = async () => {
    if (deleteToken) {
      await dispatch(organizationServiceAccountTokensActions.remove(deleteToken));
      setDeleteToken(null);
    }
  };

  const handleCopyToken = async () => {
    if (!revealedToken) return;
    try {
      await navigator.clipboard.writeText(revealedToken);
      setTokenCopied(true);
      setTimeout(() => setTokenCopied(false), 2000);
    } catch {
      // navigator.clipboard can fail in non-secure contexts; user can still
      // select+copy the visible token text.
    }
  };

  const closeRevealDialog = () => {
    setRevealedToken(null);
    setRevealedKeyContext(null);
    setTokenCopied(false);
  };

  // --- Permission picker (shared between create + edit dialogs) ---
  const togglePermission = (entry: PermissionEntry) => {
    if (!entry.assignableByCaller) return;
    setFormPermissions((prev) => {
      const next = new Set(prev);
      if (next.has(entry.key)) next.delete(entry.key);
      else next.add(entry.key);
      return next;
    });
  };

  const toggleGroup = (entries: PermissionEntry[]) => {
    const grantable = entries.filter((e) => e.assignableByCaller);
    if (grantable.length === 0) return;
    setFormPermissions((prev) => {
      const next = new Set(prev);
      const allSelected = grantable.every((e) => next.has(e.key));
      if (allSelected) {
        for (const e of grantable) next.delete(e.key);
      } else {
        for (const e of grantable) next.add(e.key);
      }
      return next;
    });
  };

  const renderPermissionPicker = () => {
    if (!registryLoaded) {
      return (
        <View className="flex items-center justify-center py-4">
          <Spinner className="!text-brand" />
        </View>
      );
    }
    return (
      <View className="flex flex-col gap-3">
        {groupedPermissions.map(({ group, entries }) => {
          const grantableEntries = entries.filter((e) => e.assignableByCaller);
          const allGrantableSelected = grantableEntries.length > 0 && grantableEntries.every((e) => formPermissions.has(e.key));
          return (
            <Card
              key={group}
              className="p-3"
            >
              <View className="flex flex-row items-center justify-between mb-2">
                <Text className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">{group}</Text>
                {grantableEntries.length > 0 ? (
                  <button
                    onClick={() => toggleGroup(entries)}
                    className="text-xs text-brand dark:text-brand-light hover:underline"
                    type="button"
                  >
                    {allGrantableSelected ? "Deselect all" : "Select all"}
                  </button>
                ) : null}
              </View>
              <View className="flex flex-col gap-1.5">
                {entries.map((entry) => {
                  const disabled = !entry.assignableByCaller;
                  return (
                    <View
                      key={entry.key}
                      className="flex flex-row items-start gap-2"
                    >
                      <CheckboxField
                        value={formPermissions.has(entry.key)}
                        onChange={() => togglePermission(entry)}
                        isDisabled={disabled}
                        tooltip={disabled ? "You don't hold this permission yourself, so you can't grant it." : undefined}
                        className="!h-4 !w-4 !min-w-[16px] mt-0.5"
                        labelClassName="!w-auto"
                      />
                      <View className={cn("flex flex-col", disabled && "opacity-50")}>
                        <Text className="text-sm text-gray-700 dark:text-gray-300 font-mono">{entry.key}</Text>
                        <Text className="text-xs text-gray-500 dark:text-gray-400">{entry.description}</Text>
                      </View>
                    </View>
                  );
                })}
              </View>
            </Card>
          );
        })}
      </View>
    );
  };

  const renderFormDialogBody = () => (
    <View className="p-4 flex flex-col gap-4 max-h-[70vh] overflow-y-auto no-scrollbar">
      <InputField
        label="Name"
        value={formName}
        onChange={setFormName}
        placeholder="e.g. Production ingestion"
      />
      <InputField
        label="Description (optional)"
        value={formDescription}
        onChange={setFormDescription}
        placeholder="What's this account used for?"
      />

      <View className="flex flex-col gap-2">
        <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">Permissions</Text>
        <Text className="text-xs text-gray-500 dark:text-gray-400">
          Select the permissions this service account should hold. You can only grant permissions you yourself hold. Tokens minted on this account will inherit
          these permissions.
        </Text>
        {renderPermissionPicker()}
      </View>

      {formError ? <Text className="text-sm text-red-500 dark:text-red-400">{formError}</Text> : null}
    </View>
  );

  // --- Render ---
  const activeAccountForMenu = openMenuAccountId ? (accounts.find((a) => a.id === openMenuAccountId) ?? null) : null;
  const currentTokens: OrganizationServiceAccountToken[] = tokensAccount ? Object.values(tokensByAccount[tokensAccount.id] ?? {}) : [];

  return (
    <OrganizationSettingsTab
      title="Service Accounts"
      tooltip="Non-human identities scoped to this organization. Each has an explicit permission set and can hold multiple tokens — issue a new one and revoke the old to rotate credentials without re-permissioning consumers. Tokens use the vrst_ prefix."
      count={accounts.length}
      className={className}
    >
      <SettingsList>
        {isLoadingAccounts && accounts.length === 0 ? (
          <View className="flex items-center justify-center py-6">
            <Spinner className="!text-brand" />
          </View>
        ) : (
          <>
            {accounts.map((account) => {
              const permCount = account.permissions.length;
              return (
                <SettingsListItem
                  key={account.id}
                  icon="key"
                  title={account.name}
                  subtitle={account.description || `Created ${new Date(account.createdAt).toLocaleDateString()}`}
                >
                  <View className="flex flex-row items-center gap-2 w-full sm:w-auto justify-end">
                    <Text
                      variant="body"
                      className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                    >
                      {permCount} permission{permCount === 1 ? "" : "s"}
                    </Text>
                    <button
                      ref={(el) => {
                        triggerRefs.current[account.id] = el;
                      }}
                      type="button"
                      onClick={() => toggleMenu(account.id)}
                      title="More actions"
                      className="w-7 h-7 rounded-full flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                      <Icon
                        name="more-vertical"
                        size={16}
                      />
                    </button>
                  </View>
                </SettingsListItem>
              );
            })}

            <button
              onClick={openCreateDialog}
              className="flex flex-row items-center gap-2 px-4 py-3 w-full text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group"
            >
              <View className="flex items-center justify-center w-8 h-8 rounded-full border-2 border-dashed border-gray-300 dark:border-gray-600 group-hover:border-brand dark:group-hover:border-brand-light transition-colors">
                <Icon
                  name="add"
                  size={14}
                  className="text-gray-400 dark:text-gray-500 group-hover:text-brand dark:group-hover:text-brand-light transition-colors"
                />
              </View>
              <Text className="text-sm text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors">
                Create service account
              </Text>
            </button>
          </>
        )}
      </SettingsList>

      {openMenuAccountId &&
        menuPosition &&
        activeAccountForMenu &&
        ReactDOM.createPortal(
          <View
            ref={menuRef}
            style={{ position: "fixed", top: menuPosition.top, right: menuPosition.right }}
            className="min-w-[12rem] bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden z-[10000]"
          >
            <button
              type="button"
              onClick={() => openEditDialog(activeAccountForMenu)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors text-left"
            >
              <Icon
                name="edit"
                size={14}
              />
              Edit permissions
            </button>
            <button
              type="button"
              onClick={() => openTokensDialog(activeAccountForMenu)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors text-left border-t border-gray-100 dark:border-gray-800"
            >
              <Icon
                name="key"
                size={14}
              />
              Manage tokens
            </button>
            <button
              type="button"
              onClick={() => openDeleteDialog(activeAccountForMenu)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-left border-t border-gray-100 dark:border-gray-800"
            >
              <Icon
                name="trash"
                size={14}
              />
              Delete
            </button>
          </View>,
          document.body,
        )}

      {/* --- Create dialog --- */}
      <Dialog
        isOpen={showCreateDialog}
        onClose={() => {
          if (!isCreatingAccount) {
            setShowCreateDialog(false);
            resetForm();
          }
        }}
        title="Create Service Account"
        size="xl"
        className="!max-w-3xl"
      >
        {renderFormDialogBody()}
        <View className="px-4 pb-4 flex flex-row gap-2 justify-end pt-2 border-t border-gray-200 dark:border-gray-700">
          <Button
            variant="neutral"
            size="auto"
            onClick={() => {
              setShowCreateDialog(false);
              resetForm();
            }}
            disabled={isCreatingAccount}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            size="auto"
            onClick={handleCreate}
            disabled={isCreatingAccount || !formName.trim() || formPermissions.size === 0}
          >
            <Icon
              name={isCreatingAccount ? "loader" : "add"}
              size={14}
              className={isCreatingAccount ? "animate-spin" : ""}
            />
            {isCreatingAccount ? "Creating..." : "Create Account"}
          </Button>
        </View>
      </Dialog>

      {/* --- Edit dialog --- */}
      <Dialog
        isOpen={!!editingAccount}
        onClose={() => {
          if (!isSavingEdit) {
            setEditingAccount(null);
            resetForm();
          }
        }}
        title={
          editingAccount ? (
            <View className="flex flex-col gap-0.5 min-w-0">
              <Text className="text-lg font-semibold truncate">{editingAccount.name} — Service Account Permissions</Text>
              {editingAccount.description ? <Text className="text-xs text-gray-500 dark:text-gray-400 truncate">{editingAccount.description}</Text> : null}
            </View>
          ) : (
            "Service Account Permissions"
          )
        }
        size="xl"
        className="!max-w-3xl"
      >
        {renderFormDialogBody()}
        <View className="px-4 pb-4 flex flex-row gap-2 justify-end pt-2 border-t border-gray-200 dark:border-gray-700">
          <Button
            variant="neutral"
            size="auto"
            onClick={() => {
              setEditingAccount(null);
              resetForm();
            }}
            disabled={isSavingEdit}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            size="auto"
            onClick={handleSaveEdit}
            disabled={isSavingEdit || !formName.trim() || formPermissions.size === 0}
          >
            <Icon
              name={isSavingEdit ? "loader" : "check"}
              size={14}
              className={isSavingEdit ? "animate-spin" : ""}
            />
            {isSavingEdit ? "Saving..." : "Save changes"}
          </Button>
        </View>
      </Dialog>

      {/* --- Tokens dialog --- */}
      <Dialog
        isOpen={!!tokensAccount}
        onClose={() => {
          setTokensAccount(null);
          setNewTokenName("");
        }}
        title={
          tokensAccount ? (
            <View className="flex flex-col gap-0.5 min-w-0">
              <Text className="text-lg font-semibold truncate">{tokensAccount.name} — Service Account Tokens</Text>
              {tokensAccount.description ? <Text className="text-xs text-gray-500 dark:text-gray-400 truncate">{tokensAccount.description}</Text> : null}
            </View>
          ) : (
            "Service Account Tokens"
          )
        }
        size="lg"
      >
        <View className="p-4 flex flex-col gap-4">
          {tokensAccount ? (
            <>
              {/* Token list */}
              <View className="flex flex-col gap-2">
                <Text className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">Active tokens</Text>
                {currentTokens.length === 0 ? (
                  <Text className="text-sm text-gray-400 dark:text-gray-500">No tokens issued yet. Mint one below.</Text>
                ) : (
                  currentTokens.map((token: OrganizationServiceAccountToken) => (
                    <View
                      key={token.id}
                      className="flex flex-row items-center justify-between gap-2 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800/50"
                    >
                      <View className="flex flex-col min-w-0">
                        <Text className="text-sm text-gray-700 dark:text-gray-300 truncate">{token.name || "Unnamed token"}</Text>
                        <Text className="text-xs text-gray-400 dark:text-gray-500">
                          {token.lastUsedAt ? `Last used ${new Date(token.lastUsedAt).toLocaleDateString()}` : "Never used"}
                          {" · "}
                          Created {new Date(token.createdAt).toLocaleDateString()}
                        </Text>
                      </View>
                      <Button
                        variant="danger"
                        size="auto"
                        onClick={() => setDeleteToken({ serviceAccountId: tokensAccount.id, tokenId: token.id })}
                      >
                        <Icon
                          name="trash"
                          size={12}
                        />
                        Revoke
                      </Button>
                    </View>
                  ))
                )}
              </View>

              {/* Mint */}
              <View className="flex flex-col gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                <Text className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">Mint new token</Text>
                <View className="flex flex-row items-center gap-2">
                  <InputField
                    label=""
                    value={newTokenName}
                    onChange={setNewTokenName}
                    placeholder="Token name (optional, e.g. rotated-2026-04)"
                    className="flex-1"
                  />
                  <Button
                    variant="success"
                    size="auto"
                    onClick={handleMintToken}
                    disabled={isMinting}
                  >
                    <Icon
                      name={isMinting ? "loader" : "add"}
                      size={14}
                      className={isMinting ? "animate-spin" : ""}
                    />
                    {isMinting ? "Minting..." : "Mint token"}
                  </Button>
                </View>
              </View>
            </>
          ) : null}
        </View>
      </Dialog>

      {/* --- Token reveal dialog (one-time) --- */}
      <Dialog
        isOpen={!!revealedToken}
        onClose={closeRevealDialog}
        title="Save your token"
      >
        <View className="p-4 flex flex-col gap-4">
          <Text className="text-sm text-gray-700 dark:text-gray-300">Copy this token now. For security, the full value will not be shown again.</Text>
          <View className="flex flex-row items-center gap-2 p-3 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
            <Text className="text-sm font-mono break-all flex-1 text-gray-900 dark:text-gray-100">{revealedToken}</Text>
            <Button
              variant={tokenCopied ? "success" : "info"}
              size="auto"
              onClick={handleCopyToken}
            >
              <Icon
                name={tokenCopied ? "check" : "copy"}
                size={14}
              />
              {tokenCopied ? "Copied" : "Copy"}
            </Button>
          </View>
          {revealedKeyContext ? (
            <Text className="text-xs text-gray-500 dark:text-gray-400">
              Token{revealedKeyContext.tokenName ? ` "${revealedKeyContext.tokenName}"` : ""} issued for service account &ldquo;{revealedKeyContext.accountName}
              &rdquo;. Use the <Text className="font-mono text-xs">Authorization: Bearer</Text> header to authenticate API requests.
            </Text>
          ) : null}
          <View className="flex flex-row justify-end pt-2 border-t border-gray-200 dark:border-gray-700">
            <Button
              variant="neutral"
              size="auto"
              onClick={closeRevealDialog}
            >
              Done
            </Button>
          </View>
        </View>
      </Dialog>

      <ConfirmDialog
        isOpen={!!deleteAccountId}
        onClose={() => setDeleteAccountId(null)}
        onConfirm={handleConfirmDeleteAccount}
        title="Delete Service Account"
        message="Are you sure? All tokens attached to this account will stop working immediately, and any application using them will be locked out. This cannot be undone."
      />

      <ConfirmDialog
        isOpen={!!deleteToken}
        onClose={() => setDeleteToken(null)}
        onConfirm={handleConfirmDeleteToken}
        title="Revoke Token"
        message="Are you sure? Any application using this token will immediately stop working. The service account itself stays active — other tokens (if any) keep working."
      />
    </OrganizationSettingsTab>
  );
};
